import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Spinner from 'react-bootstrap/Spinner';

import { URLS } from '../../resources/constants';
import { useForecastApi } from '../../resources/hooks/api/useForecastApiHook';
import ForecastLayout, { ForecastPage } from './ForecastLayout';
import { money } from './ForecastResults';
import { MONTH_FULL_NAMES } from './forecastUtils';

export type DetailKind = 'income_detail' | 'cogs_detail' | 'expense_detail' | 'payroll';

interface Series { monthly: number[]; yearly: number[]; total: number }
interface Row {
    key: string;
    code?: string | number | null;
    label: string;
    values: number[];          // 12 months + year total
    cls?: string;
    format?: 'money' | 'count';
}

const DETAIL: Record<DetailKind, { title: string; subtitle: string; rule: string; tab: string }> = {
    income_detail: {
        title: 'Income Detail',
        subtitle: 'Revenue forecast detail by account and product',
        rule: 'Income adjustments are displayed as positive amounts and subtracted from income to give net revenue.',
        tab: 'income',
    },
    cogs_detail: {
        title: 'COGS Detail',
        subtitle: 'Cost of goods sold forecast detail',
        rule: 'COGS is displayed as a positive amount; the calculation engine subtracts it from net revenue to give gross profit.',
        tab: 'cogs',
    },
    expense_detail: {
        title: 'Expense Detail',
        subtitle: 'Operating expense forecast detail',
        rule: 'Operating expenses display as positive amounts; the calculation engine subtracts them when calculating EBITDA.',
        tab: 'operating_expense',
    },
    payroll: {
        title: 'People & Payroll',
        subtitle: 'Payroll, taxes, benefits and staffing forecast detail',
        rule: 'Payroll and related employment costs display as positive amounts; the calculation engine subtracts them when calculating EBITDA. Headcount is the number of employees at month end.',
        tab: 'payroll',
    },
};

/**
 * Detail views of the calculated forecast for one selected year: one row per
 * forecast line (GL account + description), 12 months and the year total.
 */
const ForecastDetail: React.FC<{ kind: DetailKind }> = ({ kind }) => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const navigate = useNavigate();
    const { forecastId } = useParams();
    const id = Number(forecastId);
    const { getResults } = useForecastApi();

    const [results, setResults] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [year, setYear] = useState<number>(1);
    const [showUnits, setShowUnits] = useState<boolean>(true);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getResults(id, (_m: string, resp: any) => {
            setResults(resp?.data?.data);
            setLoading(false);
        }, (msg: string) => {
            toast.error(msg);
            setLoading(false);
        });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Detail pages share one mounted component - start each at Year 1.
    useEffect(() => {
        setYear(1);
    }, [kind]);

    const meta = DETAIL[kind];
    const tr = (key: string, fallback: string, opts?: any) => String(t(key, fallback, opts));
    const page = kind as ForecastPage;

    if (loading || !results) {
        return (
            <ForecastLayout active={page} forecastId={id}>
                <div className="fyf-empty"><Spinner animation="border" /></div>
            </ForecastLayout>
        );
    }

    const lo = (year - 1) * 12;
    const hi = year * 12;
    const S = results.summary;
    const lines: any[] = results.lines || [];

    /** 12 months of the selected year + the year total (sum, or a balance's year-end value). */
    const yearCols = (s: Series, kindOf: 'flow' | 'balance' | 'opening' = 'flow', scale = 1): number[] => {
        const months = s.monthly.slice(lo, hi).map((v) => v * scale);
        const total = kindOf === 'flow' ? (s.yearly[year - 1] * scale)
            : kindOf === 'balance' ? s.monthly[hi - 1] * scale : s.monthly[lo] * scale;
        return [...months, total];
    };
    const countCols = (vals: number[], total: 'sum' | 'last' | 'avg'): number[] => {
        const months = vals.slice(lo, hi);
        const tot = total === 'sum' ? months.reduce((a, b) => a + b, 0)
            : total === 'last' ? months[months.length - 1] : months.reduce((a, b) => a + b, 0) / months.length;
        return [...months, tot];
    };
    const lineRows = (category: string, sign = false): Row[] => lines
        .filter((l) => l.category === category)
        .flatMap((l) => {
            const out: Row[] = [{
                key: `l-${l.id}`,
                code: l.account_number,
                label: `${l.name}${l.description ? ` / ${l.description}` : ''}`,
                values: yearCols(l, 'flow', sign ? l.sign : 1),
            }];
            if (showUnits && l.units) {
                const isSub = l.method === 'subscription';
                out.push({
                    key: `u-${l.id}`,
                    label: isSub ? tr('detail.activeSubscribers', 'Active subscribers (month end)') : tr('detail.unitsSold', 'Units sold'),
                    values: countCols(l.units, isSub ? 'last' : 'sum'),
                    cls: 'fyf-sub-row',
                    format: 'count',
                });
            }
            return out;
        });
    const section = (key: string, label: string): Row => ({ key: `s-${key}`, label, values: [], cls: 'fyf-section' });
    const total = (key: string, label: string, s: Series, kindOf: 'flow' | 'balance' | 'opening' = 'flow', scale = 1): Row =>
        ({ key: `t-${key}`, label, values: yearCols(s, kindOf, scale), cls: 'fyf-total' });

    let rows: Row[] = [];
    if (kind === 'income_detail') {
        rows = [
            section('income', tr('category.income', 'Gross Operating Income')),
            ...lineRows('income'),
            total('income', tr('detail.totalIncome', 'Total Income'), S.gross_revenue),
            section('adj', tr('category.income_adjustment', 'Income Adjustments')),
            ...lineRows('income_adjustment'),
            total('adj', tr('detail.totalAdjustments', 'Total Adjustments'), S.income_adjustments),
            total('net', tr('results.netRevenue', 'Net Revenue'), S.net_revenue),
        ];
    } else if (kind === 'cogs_detail') {
        rows = [
            ...lineRows('cogs'),
            total('cogs', tr('detail.totalCogs', 'Total COGS'), S.cogs),
            total('gp', tr('results.grossProfit', 'Gross Profit'), S.gross_profit),
        ];
    } else if (kind === 'expense_detail') {
        rows = [
            ...lineRows('operating_expense'),
            total('opex', tr('detail.totalOpex', 'Total Operating Expenses'), S.operating_expenses),
        ];
    } else {
        rows = [
            ...lineRows('payroll'),
            total('payroll', tr('detail.totalPayroll', 'Total Payroll'), S.payroll),
            ...(showUnits ? [{ key: 'headcount', label: tr('detail.headcount', 'Headcount (month end)'),
              values: countCols(results.headcount.monthly, 'last'), cls: 'fyf-sub-row', format: 'count' as const }] : []),
        ];
    }

    const hasLines = lines.some((l) => ({
        income_detail: ['income', 'income_adjustment'], cogs_detail: ['cogs'], expense_detail: ['operating_expense'],
        payroll: ['payroll'],
    } as Record<DetailKind, string[]>)[kind].includes(l.category));
    const months = results.months.slice(lo, hi).map((m: any) => m.label.split(' ')[0]);
    const showCodeCol = true;
    const f = results.forecast;
    /** Calendar year a forecast year starts in, e.g. 2027 for a Year 1 starting Oct 2027. */
    const yearLabel = (n: number): string =>
        String(Number(f.start_year) + Math.floor((Number(f.start_month) - 1 + (n - 1) * 12) / 12));

    return (
        <ForecastLayout active={page} forecastId={id}>
            <div className="d-flex justify-content-between align-items-start gap-3">
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <button type="button" className="fyf-back" onClick={() => navigate(URLS.FIVE_YEAR_FORECAST)}>
                        ← {t('entry.back', 'All forecasts')}
                    </button>
                    <h1>{tr(`nav.${kind}`, meta.title)}</h1>
                    <div className="fyf-sub mb-1">{tr(`detail.${kind}.subtitle`, meta.subtitle)}</div>
                    <div className="fyf-meta">
                        <strong className="text-dark text-break">{f.name}</strong>
                        {' · '}{t('entry.starts', 'Starts {{month}} {{year}}', { month: MONTH_FULL_NAMES[f.start_month - 1], year: f.start_year })}
                    </div>
                </div>
                <div className="fyf-actions mt-0 flex-shrink-0 justify-content-end">
                    {['income_detail', 'payroll'].includes(kind) && (
                        <button type="button" className="fyf-secondary" onClick={() => setShowUnits((v) => !v)}>
                            {kind === 'payroll'
                                ? (showUnits ? tr('detail.hideHeadcount', 'Hide headcount') : tr('detail.showHeadcount', 'Show headcount'))
                                : (showUnits ? tr('detail.hideUnits', 'Hide units & headcount') : tr('detail.showUnits', 'Show units & headcount'))}
                        </button>
                    )}
                    <button type="button" className="fyf-primary"
                        onClick={() => navigate(`${URLS.FIVE_YEAR_FORECAST_ENTRY.replace(':forecastId', String(id))}?tab=${meta.tab}`)}>
                        {t('results.editEntries', 'Edit Entries')}
                    </button>
                </div>
            </div>

            <div className="fyf-yearsel">
                {[1, 2, 3, 4, 5].map((y) => (
                    <button key={y} type="button" className={y === year ? 'on' : ''} onClick={() => setYear(y)}>
                        {yearLabel(y)}
                    </button>
                ))}
            </div>

            <div className="fyf-panel">
                {!hasLines ? (
                    <div className="fyf-empty">{tr('detail.noLines', 'Nothing has been entered for this view yet.')}</div>
                ) : (
                    <div className="fyf-table-wrap fyf-results">
                        <table>
                            <thead>
                                <tr>
                                    {showCodeCol && <th>{t('entry.colAccount', 'GL COA')}</th>}
                                    <th>{t('entry.colDescription', 'Description / Product')}</th>
                                    {months.map((m: string, i: number) => <th key={`${m}-${i}`} className="num">{m}</th>)}
                                    <th className="num">{tr('results.totalCol', 'Total')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (r.cls === 'fyf-section' ? (
                                    <tr key={r.key} className="fyf-section"><td colSpan={months.length + (showCodeCol ? 3 : 2)}>{r.label}</td></tr>
                                ) : (
                                    <tr key={r.key} className={r.cls || ''}>
                                        {showCodeCol && <td className="text-nowrap fw-bold">{r.code || ''}</td>}
                                        <td>{r.label}</td>
                                        {r.values.map((v, i) => (
                                            <td key={i} className={`num${v < 0 ? ' neg' : ''}`}>
                                                {r.format === 'count'
                                                    ? Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
                                                    : money(v)}
                                            </td>
                                        ))}
                                    </tr>
                                )))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="fyf-note">
                    <b>{tr('detail.ruleTitle', 'Reporting rule')}</b><br />
                    {tr(`detail.${kind}.rule`, meta.rule)}
                </div>
            </div>
        </ForecastLayout>
    );
};

export default ForecastDetail;
